"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { motion } from "framer-motion";
import { register } from "@/app/actions/auth";
import Link from "next/link";
import {
  Building2,
  Lock,
  Mail,
  MapPin,
  Phone,
  User,
  FileText,
  Building,
} from "lucide-react";
import toast from "react-hot-toast";

type RegisterData = {
  username: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  full_name: string;
  company: string;
  phone: string;
  tax_number: string;
  address: string;
  city: string;
  district: string;
};

export default function RegisterPage() {
  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnUrl = searchParams.get("returnUrl");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data: RegisterData = {
      username: formData.get("username") as string,
      email: formData.get("email") as string,
      password: formData.get("password") as string,
      first_name: formData.get("first_name") as string,
      last_name: formData.get("last_name") as string,
      full_name: `${formData.get("first_name")} ${formData.get("last_name")}`,
      company: formData.get("company") as string,
      phone: formData.get("phone") as string,
      tax_number: formData.get("tax_number") as string,
      address: formData.get("address") as string,
      city: formData.get("city") as string,
      district: formData.get("district") as string,
    };

    try {
      setIsLoading(true);
      setError(undefined);
      const result = await register(data);
      if (result.error) {
        setError(result.error);
      } else {
        toast.success("Kayıt başarılı! Giriş yapabilirsiniz.", {
          duration: 3000,
          style: {
            background: "#10B981",
            color: "#fff",
          },
        });

        // Redirect to login page with return URL if available
        if (returnUrl) {
          router.push(`/giris-yap?returnUrl=${encodeURIComponent(returnUrl)}`);
        } else {
          router.push("/giris-yap");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-orange-100 to-blue-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-grid-black/[0.02] bg-[size:20px_20px]" />
      </div>

      <div className="container max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-4xl mx-auto space-y-8 bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-xl border border-white/20"
        >
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">
              Kayıt Ol
            </h1>
            <p className="text-gray-600 text-lg animate-pulse">Sadece fotoğrafçıların üyelikleri kabul edilmektedir. Bireysel satışımız yoktur!</p>
            {returnUrl && (
              <div className="mt-2 text-sm text-orange-600 bg-orange-50 p-2 rounded-md">
                Siparişinize devam etmek için lütfen kayıt olun
              </div>
            )}
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 text-sm text-red-800 bg-red-50 border border-red-100 rounded-xl"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Kullanıcı Adı
                </label>
                <div className="relative">
                  <Input
                    name="username"
                    type="text"
                    required
                    className="pl-10 bg-white/60"
                    placeholder="Kullanıcı adınızı girin"
                    disabled={isLoading}
                  />
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  E-posta
                </label>
                <div className="relative">
                  <Input
                    name="email"
                    type="email"
                    required
                    className="pl-10 bg-white/60"
                    placeholder="E-posta adresinizi girin"
                    disabled={isLoading}
                  />
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Ad
                </label>
                <div className="relative">
                  <Input
                    name="first_name"
                    type="text"
                    required
                    className="pl-10 bg-white/60"
                    placeholder="Adınızı girin"
                    disabled={isLoading}
                  />
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Soyad
                </label>
                <div className="relative">
                  <Input
                    name="last_name"
                    type="text"
                    required
                    className="pl-10 bg-white/60"
                    placeholder="Soyadınızı girin"
                    disabled={isLoading}
                  />
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Şifre
                </label>
                <div className="relative">
                  <Input
                    name="password"
                    type="password"
                    required
                    className="pl-10 bg-white/60"
                    placeholder="Şifrenizi girin"
                    disabled={isLoading}
                  />
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Firma Adı
                </label>
                <div className="relative">
                  <Input
                    name="company"
                    type="text"
                    required
                    className="pl-10 bg-white/60"
                    placeholder="Firma adını girin"
                    disabled={isLoading}
                  />
                  <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Telefon
                </label>
                <div className="relative">
                  <Input
                    name="phone"
                    type="tel"
                    required
                    className="pl-10 bg-white/60"
                    placeholder="Telefon numaranızı girin"
                    disabled={isLoading}
                  />
                  <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Vergi Numarası/TC Kimlik Numarası
                </label>
                <div className="relative">
                  <Input
                    name="tax_number"
                    type="text"
                    required
                    className="pl-10 bg-white/60"
                    placeholder="Vergi numaranızı veya TC Kimlik numaranızı girin"
                    disabled={isLoading}
                  />
                  <FileText className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Adres
                </label>
                <div className="relative">
                  <Input
                    name="address"
                    type="text"
                    required
                    className="pl-10 bg-white/60"
                    placeholder="Adresinizi girin"
                    disabled={isLoading}
                  />
                  <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Şehir
                </label>
                <div className="relative">
                  <Input
                    name="city"
                    type="text"
                    required
                    className="pl-10 bg-white/60"
                    placeholder="Şehir girin"
                    disabled={isLoading}
                  />
                  <Building className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  İlçe
                </label>
                <div className="relative">
                  <Input
                    name="district"
                    type="text"
                    required
                    className="pl-10 bg-white/60"
                    placeholder="İlçe girin"
                    disabled={isLoading}
                  />
                  <Building className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-500 hover:from-orange-600 hover:to-orange-600 text-white font-medium py-2.5"
              >
                {isLoading ? "Kayıt yapılıyor..." : "Kayıt Ol"}
              </Button>

              <p className="text-center text-sm text-gray-600">
                Kayıt olarak{" "}
                <Link
                  href="/gizlilik"
                  className="text-rose-600 hover:text-rose-700"
                >
                  Gizlilik Politikası
                </Link>{" "}
                ve{" "}
                <Link
                  href="/kullanim-kosullari"
                  className="text-rose-600 hover:text-rose-700"
                >
                  Kullanım Koşulları
                </Link>
                'nı kabul etmiş olursunuz.
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
